import * as React from 'react';

import type { Block } from '@gitbook/slate';
import { assert } from '@gitbook/util-ts';

type BlockStepperContextType = {
    /**
     * Get the index of a step in the stepper.
     */
    getStepIndex: (nodeKey: string) => number;
};

// This context is needed to be sure we refresh the stepper step when a new step is added or removed.
// Since our editor is very optimized, else the stepper step would not be refreshed.
const BlockStepperContext = React.createContext<BlockStepperContextType | null>(null);

/**
 * Component to render a stepper block.
 */
export function BlockStepperContextProvider(props: {
    stepperBlock: Block;
    children: React.ReactNode;
}) {
    const { stepperBlock } = props;

    const value = React.useMemo<BlockStepperContextType>(
        () => ({ getStepIndex: (nodeKey) => stepperBlock.getChildIndex(nodeKey) }),
        [stepperBlock]
    );

    return (
        <BlockStepperContext.Provider value={value}>{props.children}</BlockStepperContext.Provider>
    );
}

function useBlockStepperContext() {
    const ctx = React.useContext(BlockStepperContext);
    assert(ctx, 'BlockStepperContext must be used inside a BlockStepperContextProvider');
    return ctx;
}

/**
 * Hook to get the index of a step in the stepper.
 */
export function useStepIndex(nodeKey: string): number {
    const ctx = useBlockStepperContext();
    return ctx.getStepIndex(nodeKey);
}
